<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAccount } from 'dashboard/composables/useAccount';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ChannelPickerHero from './components/ChannelPickerHero.vue';
import ChannelPickerCard from './components/ChannelPickerCard.vue';
import { isChannelAvailable } from 'dashboard/helpers/channelAvailability';
import { isApiInboxDisabledForAccount } from './apiInboxVisibility';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const PICKER = 'INBOX_MGMT.ADD.AUTH.INTRO.PICKER';

const enabledFeatures = computed(() => currentAccount.value?.features || {});

const isAvailable = key => isChannelAvailable(key, enabledFeatures.value);

// Fixed channel colors, dark enough for white icons in both themes.
const pickerChannel = (key, { titleKey = key, color, icon, hintIcon }) => ({
  key,
  title: t(`INBOX_MGMT.ADD.AUTH.CHANNEL.${titleKey.toUpperCase()}.TITLE`),
  description: t(`${PICKER}.CHANNELS.${key.toUpperCase()}.DESCRIPTION`),
  hint: t(`${PICKER}.CHANNELS.${key.toUpperCase()}.HINT`),
  color,
  icon,
  hintIcon,
});

const whatsappChannel = computed(() => ({
  key: 'whatsapp',
  title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
  description: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.DESCRIPTION'),
  recommended: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.RECOMMENDED'),
  badge: t('INBOX_MGMT.ADD.AUTH.INTRO.WHATSAPP_BADGE'),
  optionsTitle: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.OPTIONS_TITLE'),
  optionsHint: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.OPTIONS_HINT'),
  options: [
    {
      icon: 'i-hugeicons-smart-phone-01',
      title: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.CLOUD_TITLE'),
      description: t(
        'INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.CLOUD_DESCRIPTION'
      ),
      tag: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.CLOUD_TAG'),
      ideal: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.CLOUD_IDEAL'),
    },
    {
      icon: 'i-hugeicons-message-multiple-01',
      title: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.COEXISTENCE_TITLE'),
      description: t(
        'INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.COEXISTENCE_DESCRIPTION'
      ),
      tag: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.COEXISTENCE_TAG'),
      ideal: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.COEXISTENCE_IDEAL'),
    },
  ],
  footnote: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.FOOTNOTE'),
  ctaLabel: t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.WHATSAPP.CTA'),
  color: '#18804D',
  icon: 'i-brand-whatsapp',
}));

const channelGroups = computed(() => {
  const groups = [
    {
      key: 'social',
      icon: 'i-hugeicons-message-multiple-01',
      channels: [
        pickerChannel('facebook', {
          color: '#2F6FAE',
          icon: 'i-brand-facebook',
          hintIcon: 'i-hugeicons-comment-01',
        }),
        pickerChannel('instagram', {
          color: '#B4436A',
          icon: 'i-brand-instagram',
          hintIcon: 'i-hugeicons-comment-01',
        }),
        pickerChannel('telegram', {
          color: '#1F78A3',
          icon: 'i-brand-telegram',
          hintIcon: 'i-hugeicons-message-01',
        }),
      ],
    },
    {
      key: 'web',
      icon: 'i-hugeicons-globe-02',
      channels: [
        pickerChannel('email', {
          color: '#5B6B80',
          icon: 'i-hugeicons-mail-01',
          hintIcon: 'i-hugeicons-bubble-chat',
        }),
        pickerChannel('website', {
          color: '#5D5FC4',
          icon: 'i-lucide-panels-top-left',
          hintIcon: 'i-hugeicons-code',
        }),
        pickerChannel('api', {
          color: '#9A6B12',
          icon: 'i-lucide-braces',
          hintIcon: 'i-hugeicons-code',
        }),
      ],
    },
  ];

  const hideApi = isApiInboxDisabledForAccount(accountId.value);

  return groups
    .map(group => ({
      ...group,
      title: t(`${PICKER}.GROUPS.${group.key.toUpperCase()}.TITLE`),
      description: t(`${PICKER}.GROUPS.${group.key.toUpperCase()}.DESCRIPTION`),
      channels: group.channels.filter(
        channel => !(hideApi && channel.key === 'api')
      ),
    }))
    .filter(group => group.channels.length);
});

const goToInboxList = () => {
  router.push({
    name: 'settings_inbox_list',
    params: { accountId: accountId.value },
  });
};

const initChannelAuth = channel => {
  if (!isAvailable(channel)) return;

  router.push({
    name: 'settings_inboxes_page_channel',
    params: {
      sub_page: channel,
      accountId: accountId.value,
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-8 p-5 sm:p-7">
    <section class="flex flex-col gap-4 sm:flex-row sm:items-start">
      <div class="min-w-0 flex-1">
        <span
          class="inline-flex items-center gap-1.5 rounded-full bg-n-brand/10 px-2.5 py-1 text-xs font-semibold text-n-brand"
        >
          <Icon icon="i-hugeicons-bubble-chat-add" class="size-3.5" />
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.EYEBROW') }}
        </span>
        <h1 class="mt-3 text-2xl font-semibold tracking-tight text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.TITLE') }}
        </h1>
        <p class="mt-1.5 max-w-xl text-sm leading-6 text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.DESCRIPTION') }}
        </p>
      </div>

      <Button
        :label="$t('INBOX_MGMT.ADD.AUTH.INTRO.BACK_TO_CONNECTIONS')"
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="sm"
        class="shrink-0 self-start"
        @click="goToInboxList"
      />
    </section>

    <section>
      <ChannelPickerHero
        :channel="whatsappChannel"
        :disabled="!isAvailable('whatsapp')"
        @select="initChannelAuth"
      />
    </section>

    <section
      v-for="group in channelGroups"
      :key="group.key"
      :aria-labelledby="`channel-group-${group.key}`"
    >
      <div class="mb-3 flex items-center gap-2.5">
        <span
          class="flex size-8 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
        >
          <Icon :icon="group.icon" class="size-4" />
        </span>
        <div class="min-w-0">
          <h2
            :id="`channel-group-${group.key}`"
            class="text-sm font-semibold text-n-slate-12"
          >
            {{ group.title }}
          </h2>
          <p class="text-xs text-n-slate-11">
            {{ group.description }}
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <ChannelPickerCard
          v-for="channel in group.channels"
          :key="channel.key"
          :channel="channel"
          :disabled="!isAvailable(channel.key)"
          :unavailable-label="
            $t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.UNAVAILABLE')
          "
          @select="initChannelAuth"
        />
      </div>
    </section>

    <p
      class="flex items-start gap-2.5 rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3 text-xs leading-5 text-n-slate-11"
    >
      <Icon icon="i-hugeicons-bulb" class="mt-0.5 size-4 shrink-0" />
      {{ $t('INBOX_MGMT.ADD.AUTH.INTRO.PICKER.HELP') }}
    </p>
  </div>
</template>
